import { useRef, useCallback } from "react";
import { IVisitState } from "../../../types/VisitState";
import dayjs from "../../../configs/dayjs";

const STORAGE_KEY = "visit_tracker_data";

const COMPARE_FORMAT = "YYYY-DD-MM";

const getTodayKey = (): string => {
  return dayjs().format(COMPARE_FORMAT);
};

const isSameDay = (lastActivity: number): boolean => {
  return dayjs(lastActivity).format(COMPARE_FORMAT) === getTodayKey();
};

const isVisitExpired = (lastActivity: number, lifetimeMs: number): boolean => {
  return dayjs().valueOf() - lastActivity > lifetimeMs;
};

export const useVisitState = (visitLifetimeMs: number) => {
  const stateRef = useRef<IVisitState | null>(null);
  const lifetimeRef = useRef(visitLifetimeMs);

  lifetimeRef.current = visitLifetimeMs;

  const saveToStorage = useCallback((state: IVisitState): void => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    stateRef.current = state;
  }, []);

  const load = useCallback((): IVisitState | null => {
    if (stateRef.current) {
      if (
        !isSameDay(stateRef.current.lastActivity) ||
        isVisitExpired(stateRef.current.lastActivity, lifetimeRef.current)
      ) {
        stateRef.current = null;
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return stateRef.current;
    }

    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as IVisitState;

      if (
        !parsed.lastActivity ||
        !isSameDay(parsed.lastActivity) ||
        isVisitExpired(parsed.lastActivity, lifetimeRef.current)
      ) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      stateRef.current = parsed;
      return parsed;
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }, []);

  const createNewState = useCallback((): IVisitState => {
    const newState: IVisitState = {
      visitId: crypto.randomUUID(),
      lastActivity: dayjs().valueOf(),
    };
    saveToStorage(newState);

    return newState;
  }, [saveToStorage]);

  const updateActivity = useCallback((): void => {
    if (!stateRef.current) return;

    const updatedState: IVisitState = {
      ...stateRef.current,
      lastActivity: dayjs().valueOf(),
    };
    saveToStorage(updatedState);
  }, [saveToStorage]);

  const clearState = useCallback((): void => {
    localStorage.removeItem(STORAGE_KEY);
    stateRef.current = null;
  }, []);

  const getState = useCallback((): IVisitState | null => {
    return stateRef.current;
  }, []);

  const isExpired = useCallback((): boolean => {
    if (!stateRef.current) return true;
    return (
      !isSameDay(stateRef.current.lastActivity) ||
      isVisitExpired(stateRef.current.lastActivity, lifetimeRef.current)
    );
  }, []);

  return {
    getState,
    createNewState,
    updateActivity,
    load,
    clearState,
    isExpired,
  };
};
